<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import ProductInfo from './ProductInfo.vue'
import { krShort } from '../lib/format'
import { stockNote } from '../lib/stock'

const props = defineProps({
  product: { type: Object, required: true },
  inCart: { type: Number, default: 0 },
  // The first row is fetched straight away and first in line; the rest as they scroll into view.
  priority: { type: Boolean, default: false },
})
const emit = defineEmits(['update'])

const soldOut = computed(() => props.product.remaining <= 0)
const atMax = computed(() => props.inCart >= props.product.remaining)
const note = computed(() =>
  atMax.value && props.inCart > 0
    ? { tone: 'low', text: props.inCart === 1 ? 'Du har det sidste stk.' : `Du har de sidste ${props.inCart} stk.` }
    : stockNote(props.product),
)
// Main photo, plus the next one: revealed on hover with a mouse, or by tapping on touch screens.
// A sold-out product keeps to its main photo.
const photos = computed(() => props.product.images.slice(0, soldOut.value ? 1 : 2))
const flipped = ref(false)
const isTouch = () => window.matchMedia('(hover: none)').matches

// The hover photo isn't needed for the first paint: fetch it once the page is idle, or right away on intent.
const wantSecond = ref(false)
const loaded = reactive({})
let idle
const fetchSecond = () => (wantSecond.value = true)
// Waits for the page's own photos to finish first, so the hover photos never compete with them.
const whenIdle = () => {
  idle = window.requestIdleCallback ? requestIdleCallback(fetchSecond, { timeout: 2500 }) : setTimeout(fetchSecond, 300)
}
onMounted(() => {
  if (document.readyState === 'complete') whenIdle()
  else window.addEventListener('load', whenIdle, { once: true })
})
onBeforeUnmount(() => {
  window.removeEventListener('load', whenIdle)
  window.cancelIdleCallback ? cancelIdleCallback(idle) : clearTimeout(idle)
})

function onPhotoTap() {
  fetchSecond()
  if (photos.value.length > 1 && isTouch()) flipped.value = !flipped.value
}
</script>

<template>
  <article class="product" :class="{ 'product--soldout': soldOut, 'product--hover-photo': photos.length > 1 }">
    <div
      class="product__photo"
      :class="{ 'is-flipped': flipped }"
      :style="photos[0]?.placeholder ? { '--placeholder': `url(${photos[0].placeholder})` } : null"
      @click="onPhotoTap"
      @pointerenter="fetchSecond"
    >
      <template v-for="(photo, index) in photos" :key="photo.src">
        <img
          v-if="index === 0 || wantSecond"
          :loading="priority ? 'eager' : 'lazy'"
          :fetchpriority="priority && index === 0 ? 'high' : 'auto'"
          decoding="async"
          width="640"
          height="640"
          :sizes="photo.sizes"
          :srcset="photo.srcset"
          :src="photo.src"
          :alt="index === 0 ? product.name : ''"
          :class="{ 'is-loaded': priority || loaded[index] }"
          @load="loaded[index] = true"
        />
      </template>
      <div v-if="photos.length === 0" class="product__placeholder" aria-hidden="true">{{ product.name.charAt(0) }}</div>
      <span v-if="soldOut" class="product__badge">Udsolgt</span>
    </div>
    <!-- Outside the photo so its phone bottom sheet isn't trapped in the photo's stacking context. -->
    <ProductInfo :product="product" />

    <div class="product__body">
      <div class="product__head">
        <h3 class="product__name">
          {{ product.name }}<span v-if="product.unit" class="product__unit">{{ product.unit }}</span>
        </h3>
        <p class="product__price">{{ krShort(product.priceOre) }}</p>
      </div>
      <p v-if="product.description" class="product__desc">{{ product.description }}</p>
      <p v-if="!soldOut" class="product__stock" :class="`product__stock--${note.tone}`">{{ note.text }}</p>

      <div class="product__actions">
        <button v-if="inCart === 0" type="button" class="add" :disabled="soldOut" @click="emit('update', 1)">
          {{ soldOut ? 'Udsolgt' : 'Læg til side' }}
        </button>
        <div v-else class="stepper" role="group" :aria-label="`Antal ${product.name}`">
          <button type="button" aria-label="Færre" @click="emit('update', inCart - 1)">
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10" /></svg>
          </button>
          <output aria-live="polite">{{ inCart }}</output>
          <button type="button" aria-label="Flere" :disabled="atMax" @click="emit('update', inCart + 1)">
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10M8 3v10" /></svg>
          </button>
        </div>
      </div>
    </div>
  </article>
</template>
